import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FileText, Save, Plus, Trash2, Edit2, Eye, Check, AlertCircle, Upload, 
  Link, DollarSign, Building, User, X, Package, Image, Printer, Download, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { generateQuotePDF } from './quotePdfGenerator';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Currency helper
const getCurrencySymbol = (currency) => {
  switch (currency) {
    case 'JPY': return '¥';
    case 'EUR': return '€';
    case 'USD':
    default: return '$';
  }
};

const formatCurrency = (amount, currency) => {
  const symbol = getCurrencySymbol(currency);
  if (currency === 'JPY') {
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }
  return `${symbol}${amount.toFixed(2)}`;
};

const QuoteGeneratorTab = ({ 
  isDark, 
  authFetch, 
  showToast,
  quotes,
  generatedQuotes,
  setGeneratedQuotes,
  companySettings,
  setCompanySettings,
  savingCompanySettings,
  saveCompanySettings,
  fetchQuotes,
  initialEditQuote,
  onEditQuoteConsumed
}) => {
  const defaultQuote = {
    quote_request_id: null,
    customer_name: '',
    customer_company: '',
    customer_contact: '',
    customer_address: '',
    customer_phone: '',
    customer_email: '',
    customer_whatsapp: '',
    line_items: [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
    images: [],
    shipping_cost: 0,
    tax_rate: 0,
    processing_time: '10-15 Working days',
    delivery_time: '5-7 working days',
    delivery_method: 'Express',
    payment_terms: 'DAP - Delivered At Place',
    notes: '',
    currency: 'JPY',
    installation_location: 'inside',
    mounting_style: '',
    include_warranty: false,
    include_shipping: true,
    separate_items: false,
    options: []
  };

  // Quote Generator state
  const [editingGeneratedQuote, setEditingGeneratedQuote] = useState(null);
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  const [savingGeneratedQuote, setSavingGeneratedQuote] = useState(false);
  const [newGeneratedQuote, setNewGeneratedQuote] = useState(defaultQuote);

  // Load initial edit quote from prop (from Saved Quotes edit button)
  const editLoadedRef = useRef(false);
  useEffect(() => {
    if (initialEditQuote && !editLoadedRef.current) {
      editLoadedRef.current = true;
      const quote = initialEditQuote;
      setEditingGeneratedQuote({ id: quote.id, quote_number: quote.quote_number });
      setNewGeneratedQuote({
        ...defaultQuote,
        quote_request_id: quote.quote_request_id || null,
        customer_name: quote.customer_name || '',
        customer_company: quote.customer_company || '',
        customer_contact: quote.customer_contact || '',
        customer_address: quote.customer_address || '',
        customer_phone: quote.customer_phone || '',
        customer_email: quote.customer_email || '',
        customer_whatsapp: quote.customer_whatsapp || '',
        line_items: quote.line_items?.length > 0 ? quote.line_items : defaultQuote.line_items,
        images: quote.images || [],
        shipping_cost: quote.shipping_cost || 0,
        tax_rate: quote.tax_rate || 0,
        processing_time: quote.processing_time || defaultQuote.processing_time,
        delivery_time: quote.delivery_time || defaultQuote.delivery_time,
        delivery_method: quote.delivery_method || defaultQuote.delivery_method,
        payment_terms: quote.payment_terms || defaultQuote.payment_terms,
        notes: quote.notes || '',
        currency: quote.currency || 'JPY',
        installation_location: quote.installation_location || 'inside',
        mounting_style: quote.mounting_style || '',
        include_warranty: quote.include_warranty || false,
        include_shipping: quote.include_shipping !== false,
      });
      showToast('Quote loaded for editing');
    }
  }, [initialEditQuote]);

  // Check for transferred data from Quote Rebrander (via sessionStorage)
  useEffect(() => {
    const raw = sessionStorage.getItem('transferredQuoteData');
    if (raw) {
      sessionStorage.removeItem('transferredQuoteData');
      try {
        const data = JSON.parse(raw);
        setNewGeneratedQuote(prev => ({
          ...prev,
          ...data,
          quote_request_id: data.quote_request_id || null,
          line_items: data.line_items?.length > 0 ? data.line_items : prev.line_items
        }));
        showToast('Quote data loaded');
      } catch (e) {
        console.error('Error parsing transferred data:', e);
      }
    }
  }, []);
  const [uploadingQuoteImage, setUploadingQuoteImage] = useState(false);
  const [isDraggingQuoteImage, setIsDraggingQuoteImage] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [activeOptionIdx, setActiveOptionIdx] = useState(0);
  const quoteImageInputRef = useRef(null);

  // Link to Quote Request state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');

  // Calculate totals (combined mode)
  const calculateQuoteTotals = () => {
    const subtotal = newGeneratedQuote.line_items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
    const taxAmount = subtotal * (newGeneratedQuote.tax_rate / 100);
    const total = subtotal + newGeneratedQuote.shipping_cost + taxAmount;
    return { subtotal, taxAmount, total };
  };

  // Calculate option totals (separate mode)
  const calcOptionTotal = (opt) => {
    const subtotal = (opt.line_items || []).reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
    const tax = subtotal * ((opt.tax_rate || 0) / 100);
    const shipping = opt.shipping_cost || 0;
    return { subtotal, tax, shipping, total: subtotal + tax + shipping };
  };

  // --- Option management (separate mode) ---
  const makeNewOption = (idx) => ({
    id: Date.now().toString(),
    name: `Option ${idx + 1}`,
    line_items: [{ id: (Date.now() + 1).toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
    shipping_cost: 0,
    tax_rate: newGeneratedQuote.tax_rate || 0,
  });

  const ensureOptions = () => {
    if (newGeneratedQuote.options && newGeneratedQuote.options.length > 0) return;
    // Initialize with one option containing current line items
    setNewGeneratedQuote(prev => ({
      ...prev,
      options: [{
        id: Date.now().toString(),
        name: 'Option 1',
        line_items: prev.line_items.length > 0 ? prev.line_items : [{ id: (Date.now() + 1).toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
        shipping_cost: prev.shipping_cost || 0,
        tax_rate: prev.tax_rate || 0,
      }]
    }));
    setActiveOptionIdx(0);
  };

  const addOption = () => {
    setNewGeneratedQuote(prev => {
      const newOpts = [...(prev.options || []), makeNewOption((prev.options || []).length)];
      return { ...prev, options: newOpts };
    });
    setActiveOptionIdx((newGeneratedQuote.options || []).length);
  };

  const removeOption = (idx) => {
    setNewGeneratedQuote(prev => {
      const newOpts = (prev.options || []).filter((_, i) => i !== idx);
      if (newOpts.length === 0) return { ...prev, options: [makeNewOption(0)] };
      return { ...prev, options: newOpts };
    });
    setActiveOptionIdx(prev => Math.min(prev, Math.max(0, (newGeneratedQuote.options || []).length - 2)));
  };

  const updateOption = (idx, field, value) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      options: (prev.options || []).map((opt, i) => i === idx ? { ...opt, [field]: value } : opt)
    }));
  };

  const addOptionLineItem = (optIdx) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      options: (prev.options || []).map((opt, i) => i === optIdx ? {
        ...opt,
        line_items: [...opt.line_items, { id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }]
      } : opt)
    }));
  };

  const updateOptionLineItem = (optIdx, itemId, field, value) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      options: (prev.options || []).map((opt, i) => i === optIdx ? {
        ...opt,
        line_items: opt.line_items.map(item => item.id === itemId ? { ...item, [field]: value } : item)
      } : opt)
    }));
  };

  const removeOptionLineItem = (optIdx, itemId) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      options: (prev.options || []).map((opt, i) => {
        if (i !== optIdx) return opt;
        const filtered = opt.line_items.filter(item => item.id !== itemId);
        return { ...opt, line_items: filtered.length > 0 ? filtered : [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }] };
      })
    }));
  };

  // Line item functions (combined mode)
  const addLineItem = () => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      line_items: [...prev.line_items, { id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }]
    }));
  };

  const updateLineItem = (itemId, field, value) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeLineItem = (itemId) => {
    if (newGeneratedQuote.line_items.length <= 1) return;
    setNewGeneratedQuote(prev => ({
      ...prev,
      line_items: prev.line_items.filter(item => item.id !== itemId)
    }));
  };

  // Save quote
  const saveGeneratedQuote = async () => {
    setSavingGeneratedQuote(true);
    try {
      const method = editingGeneratedQuote ? 'PUT' : 'POST';
      const url = editingGeneratedQuote 
        ? `${API_URL}/api/admin/generated-quotes/${editingGeneratedQuote.id}`
        : `${API_URL}/api/admin/generated-quotes`;
      
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGeneratedQuote)
      });
      
      if (response.ok) {
        showToast(editingGeneratedQuote ? 'Quote updated' : 'Quote created');
        const quotesRes = await authFetch(`${API_URL}/api/admin/generated-quotes`);
        if (quotesRes.ok) {
          setGeneratedQuotes(await quotesRes.json());
        }
        resetQuoteForm();
      } else {
        showToast('Failed to save quote', 'error');
      }
    } catch (error) {
      showToast('Error saving quote', 'error');
    }
    setSavingGeneratedQuote(false);
  };

  // Reset form
  const resetQuoteForm = () => {
    setNewGeneratedQuote({
      quote_request_id: null,
      customer_name: '',
      customer_company: '',
      customer_contact: '',
      customer_address: '',
      customer_phone: '',
      customer_email: '',
      customer_whatsapp: '',
      line_items: [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
      images: [],
      shipping_cost: 0,
      tax_rate: 0,
      processing_time: '10-15 Working days',
      delivery_time: '5-7 working days',
      delivery_method: 'Express',
      payment_terms: 'DAP - Delivered At Place',
      notes: '',
      currency: 'JPY',
      installation_location: 'inside',
      mounting_style: '',
      include_warranty: false,
      include_shipping: true
    });
    setEditingGeneratedQuote(null);
    setShowQuotePreview(false);
  };

  // Edit quote
  const editGeneratedQuote = async (quote) => {
    setNewGeneratedQuote({
      quote_request_id: quote.quote_request_id || null,
      customer_name: quote.customer_name || '',
      customer_company: quote.customer_company || '',
      customer_contact: quote.customer_contact || '',
      customer_address: quote.customer_address || '',
      customer_phone: quote.customer_phone || '',
      customer_email: quote.customer_email || '',
      customer_whatsapp: quote.customer_whatsapp || '',
      line_items: quote.line_items?.length > 0 ? quote.line_items : [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
      images: quote.images || [],
      shipping_cost: quote.shipping_cost || 0,
      tax_rate: quote.tax_rate || 0,
      processing_time: quote.processing_time || '10-15 Working days',
      delivery_time: quote.delivery_time || '5-7 working days',
      delivery_method: quote.delivery_method || 'Express',
      payment_terms: quote.payment_terms || 'DAP - Delivered At Place',
      notes: quote.notes || '',
      currency: quote.currency || 'USD',
      installation_location: quote.installation_location || 'inside',
      mounting_style: quote.mounting_style || '',
      include_warranty: quote.include_warranty || false,
      include_shipping: quote.include_shipping !== false
    });
    setEditingGeneratedQuote(quote);
    
    if (quote.quote_request_id) {
      const linkedRequest = quotes.find(q => q.id === quote.quote_request_id);
      if (linkedRequest) {
        const existingImageUrls = new Set((quote.images || []).map(img => img.url));
        const newImages = [];
        
        if (linkedRequest.attachments && linkedRequest.attachments.length > 0) {
          linkedRequest.attachments.forEach((att, idx) => {
            if ((att.type?.startsWith('image/') || att.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && !existingImageUrls.has(att.url)) {
              newImages.push({
                id: Date.now() + idx,
                url: att.url,
                name: att.name || `Image ${idx + 1}`
              });
            }
          });
        }
        
        if (newImages.length > 0) {
          setNewGeneratedQuote(prev => ({
            ...prev,
            images: [...(prev.images || []), ...newImages]
          }));
          showToast(`Loaded ${newImages.length} image(s) from linked quote request`);
        }
      }
    }
  };

  // Delete quote
  const deleteGeneratedQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      const response = await authFetch(`${API_URL}/api/admin/generated-quotes/${quoteId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setGeneratedQuotes(generatedQuotes.filter(q => q.id !== quoteId));
        showToast('Quote deleted');
      } else {
        showToast('Failed to delete quote', 'error');
      }
    } catch (error) {
      showToast('Error deleting quote', 'error');
    }
  };

  // Image upload functions
  const handleQuoteImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingQuoteImage(true);
    const newImages = [];
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await authFetch(`${API_URL}/api/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          newImages.push({
            id: Date.now() + Math.random(),
            url: data.url,
            name: file.name
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
    
    if (newImages.length > 0) {
      setNewGeneratedQuote(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));
      showToast(`${newImages.length} image(s) uploaded`);
    }
    
    setUploadingQuoteImage(false);
  };

  const handleQuoteImageDrop = (e) => {
    e.preventDefault();
    setIsDraggingQuoteImage(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) handleQuoteImageUpload(files);
  };

  const handleQuoteImageDragOver = (e) => {
    e.preventDefault();
    setIsDraggingQuoteImage(true);
  };

  const handleQuoteImageDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingQuoteImage(false);
  };

  const removeQuoteImage = (imageId) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  // Link quote to an existing quote request
  const linkToQuoteRequest = (quoteRequest) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      quote_request_id: quoteRequest.id,
      customer_name: prev.customer_name || quoteRequest.name || '',
      customer_email: prev.customer_email || quoteRequest.email || '',
      customer_phone: prev.customer_phone || quoteRequest.phone || ''
    }));
    setShowLinkModal(false);
    showToast(`Linked to quote request from ${quoteRequest.name}`);
  };

  // Save current quote as a new quote request
  const saveAsQuoteRequest = async () => {
    if (!newGeneratedQuote.customer_name) {
      showToast('Please enter a customer name first', 'error');
      return;
    }
    
    try {
      const quoteRequestData = {
        name: newGeneratedQuote.customer_name,
        email: newGeneratedQuote.customer_email || '',
        phone: newGeneratedQuote.customer_phone || '',
        company: newGeneratedQuote.customer_company || '',
        sign_type: newGeneratedQuote.line_items?.[0]?.item_type || 'Custom Sign',
        message: newGeneratedQuote.notes || newGeneratedQuote.line_items?.[0]?.description || '',
        status: 'pending'
      };
      
      const response = await authFetch(`${API_URL}/api/admin/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteRequestData)
      });
      
      if (response.ok) {
        const newRequest = await response.json();
        
        const quoteData = {
          ...newGeneratedQuote,
          quote_request_id: newRequest.id
        };
        
        const saveResponse = await authFetch(`${API_URL}/api/admin/generated-quotes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(quoteData)
        });
        
        if (saveResponse.ok) {
          const savedQuote = await saveResponse.json();
          if (fetchQuotes) fetchQuotes();
          const genQuotesRes = await authFetch(`${API_URL}/api/admin/generated-quotes`);
          if (genQuotesRes.ok) {
            setGeneratedQuotes(await genQuotesRes.json());
          }
          showToast(`Created quote request and saved quote (${savedQuote.quote_number})`);
          resetQuoteForm();
        } else {
          if (fetchQuotes) fetchQuotes();
          showToast('Quote request created, but failed to save generated quote', 'error');
        }
      } else {
        showToast('Failed to create quote request', 'error');
      }
    } catch (error) {
      console.error('Error creating quote request:', error);
      showToast('Error creating quote request', 'error');
    }
  };

  // Get filtered quote requests for link modal
  const getFilteredQuoteRequests = () => {
    if (!linkSearchQuery) return quotes;
    const query = linkSearchQuery.toLowerCase();
    return quotes.filter(q => 
      q.name?.toLowerCase().includes(query) ||
      q.email?.toLowerCase().includes(query) ||
      q.company?.toLowerCase().includes(query) ||
      q.sign_type?.toLowerCase().includes(query)
    );
  };

  // PDF Download function using shared utility
  const downloadPDF = useCallback(async () => {
    setGeneratingPDF(true);
    try {
      const quoteData = {
        ...newGeneratedQuote,
        quote_number: editingGeneratedQuote?.quote_number || 'NEW',
        created_at: editingGeneratedQuote?.created_at || new Date().toISOString(),
      };
      await generateQuotePDF(quoteData, companySettings);
      showToast('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF generation error:', err);
      showToast('Failed to generate PDF', 'error');
    } finally {
      setGeneratingPDF(false);
    }
  }, [editingGeneratedQuote, newGeneratedQuote, companySettings, showToast]);

  return (
    <div className="space-y-6">
      {/* Quote Preview Modal */}
      {showQuotePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowQuotePreview(false)} />
          <div 
            className="relative w-full max-w-4xl max-h-[90vh] overflow-auto rounded-xl p-8"
            style={{ backgroundColor: '#fff', color: '#000' }}
          >
            <button 
              onClick={() => setShowQuotePreview(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10"
              data-testid="close-preview-btn"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Quote Preview Content - Captured by html2canvas */}
            <div className="quote-preview" style={{ fontFamily: 'Arial, sans-serif', backgroundColor: '#ffffff' }}>
              {/* Header */}
              <div className="flex justify-between items-start mb-8 pb-4 border-b-2 border-gray-300">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{companySettings.company_name}</h1>
                  {companySettings.company_name_local && (
                    <p className="text-gray-600">{companySettings.company_name_local}</p>
                  )}
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-bold text-purple-600">PROFORMA INVOICE</h2>
                  <p className="text-gray-600">Quote #: {editingGeneratedQuote?.quote_number || 'NEW'}</p>
                  <p className="text-gray-600">Date: {new Date().toLocaleDateString()}</p>
                </div>
              </div>
              
              {/* Company & Customer Info */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <Building className="w-4 h-4" /> SELLER
                  </h3>
                  <p className="font-semibold">{companySettings.company_name}</p>
                  <p>{companySettings.contact_person}</p>
                  <p>{companySettings.address}</p>
                  <p>Tel: {companySettings.phone}</p>
                  <p>Email: {companySettings.email}</p>
                  {companySettings.whatsapp && <p>WhatsApp: {companySettings.whatsapp}</p>}
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> BUYER
                  </h3>
                  <p className="font-semibold">{newGeneratedQuote.customer_company || newGeneratedQuote.customer_name}</p>
                  <p>{newGeneratedQuote.customer_contact}</p>
                  <p>{newGeneratedQuote.customer_address}</p>
                  <p>Tel: {newGeneratedQuote.customer_phone}</p>
                  <p>Email: {newGeneratedQuote.customer_email}</p>
                  {newGeneratedQuote.customer_whatsapp && <p>WhatsApp: {newGeneratedQuote.customer_whatsapp}</p>}
                </div>
              </div>
              
              {/* Line Items - Combined or Separate Options */}
              {!newGeneratedQuote.separate_items ? (
                <>
                  <table className="w-full mb-8 border-collapse">
                    <thead>
                      <tr className="bg-purple-600 text-white">
                        <th className="p-3 text-left">Type</th>
                        <th className="p-3 text-left">Item</th>
                        <th className="p-3 text-left">Description</th>
                        <th className="p-3 text-center">Qty</th>
                        <th className="p-3 text-right">Unit Price</th>
                        <th className="p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newGeneratedQuote.line_items.map((item, idx) => (
                        <tr key={item.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-3 border-b">{item.item_type}</td>
                          <td className="p-3 border-b font-medium">{item.item_name}</td>
                          <td className="p-3 border-b text-sm text-gray-600 whitespace-pre-wrap">{item.description}</td>
                          <td className="p-3 border-b text-center">{item.quantity}</td>
                          <td className="p-3 border-b text-right">{formatCurrency(item.unit_price, newGeneratedQuote.currency)}</td>
                          <td className="p-3 border-b text-right font-medium">{formatCurrency(item.quantity * item.unit_price, newGeneratedQuote.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-end mb-8">
                    <div className="w-64">
                      <div className="flex justify-between py-2 border-b"><span>Subtotal:</span><span>{formatCurrency(calculateQuoteTotals().subtotal, newGeneratedQuote.currency)}</span></div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Shipping:</span>
                        <span className={!newGeneratedQuote.include_shipping ? 'text-gray-400 italic' : ''}>
                          {newGeneratedQuote.include_shipping ? formatCurrency(newGeneratedQuote.shipping_cost, newGeneratedQuote.currency) : 'Not Included'}
                        </span>
                      </div>
                      {newGeneratedQuote.tax_rate > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span>Tax ({newGeneratedQuote.tax_rate}%):</span>
                          <span>{formatCurrency(calculateQuoteTotals().taxAmount, newGeneratedQuote.currency)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-3 text-lg font-bold text-purple-600">
                        <span>TOTAL:</span>
                        <span>{formatCurrency(newGeneratedQuote.include_shipping ? calculateQuoteTotals().total : (calculateQuoteTotals().subtotal + calculateQuoteTotals().taxAmount), newGeneratedQuote.currency)} {newGeneratedQuote.currency}</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Separate Options Mode - each option is its own section */
                <div className="space-y-6 mb-8">
                  {(newGeneratedQuote.options || []).map((opt, optIdx) => {
                    const t = calcOptionTotal(opt);
                    return (
                      <div key={opt.id} className="border-2 border-purple-200 rounded-lg overflow-hidden">
                        <div className="bg-purple-600 text-white px-4 py-2 font-bold text-sm">
                          {opt.name || `OPTION ${optIdx + 1}`}
                        </div>
                        <div className="p-4">
                          <table className="w-full border-collapse mb-4">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="p-2 text-left text-xs text-gray-600">Type</th>
                                <th className="p-2 text-left text-xs text-gray-600">Item</th>
                                <th className="p-2 text-left text-xs text-gray-600">Description</th>
                                <th className="p-2 text-center text-xs text-gray-600">Qty</th>
                                <th className="p-2 text-right text-xs text-gray-600">Unit Price</th>
                                <th className="p-2 text-right text-xs text-gray-600">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(opt.line_items || []).map((item, idx) => (
                                <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="p-2 border-b text-sm">{item.item_type}</td>
                                  <td className="p-2 border-b text-sm font-medium">{item.item_name}</td>
                                  <td className="p-2 border-b text-xs text-gray-600 whitespace-pre-wrap">{item.description}</td>
                                  <td className="p-2 border-b text-sm text-center">{item.quantity}</td>
                                  <td className="p-2 border-b text-sm text-right">{formatCurrency(item.unit_price, newGeneratedQuote.currency)}</td>
                                  <td className="p-2 border-b text-sm text-right font-medium">{formatCurrency((item.quantity || 0) * (item.unit_price || 0), newGeneratedQuote.currency)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="flex justify-end">
                            <div className="w-56 text-sm">
                              <div className="flex justify-between py-1 border-b"><span>Subtotal:</span><span>{formatCurrency(t.subtotal, newGeneratedQuote.currency)}</span></div>
                              {t.shipping > 0 && <div className="flex justify-between py-1 border-b"><span>Shipping:</span><span>{formatCurrency(t.shipping, newGeneratedQuote.currency)}</span></div>}
                              {t.tax > 0 && <div className="flex justify-between py-1 border-b"><span>Tax ({opt.tax_rate}%):</span><span>{formatCurrency(t.tax, newGeneratedQuote.currency)}</span></div>}
                              <div className="flex justify-between py-2 font-bold text-purple-600"><span>TOTAL:</span><span>{formatCurrency(t.total, newGeneratedQuote.currency)} {newGeneratedQuote.currency}</span></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Quote Options Section */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-4 bg-purple-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Installation</p>
                  <p className="font-semibold text-purple-700">{newGeneratedQuote.installation_location === 'inside' ? 'Indoor' : 'Outdoor'}</p>
                </div>
                {newGeneratedQuote.mounting_style && (
                  <div className="text-center">
                    <p className="text-xs text-gray-500 uppercase">Mounting</p>
                    <p className="font-semibold text-purple-700">{newGeneratedQuote.mounting_style}</p>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Warranty</p>
                  <p className="font-semibold text-purple-700">{newGeneratedQuote.include_warranty ? '1 Year Included' : 'Not Included'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Shipping</p>
                  <p className="font-semibold text-purple-700">{newGeneratedQuote.include_shipping ? 'Included' : 'Not Included'}</p>
                </div>
              </div>
              
              {/* Delivery & Terms */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">DELIVERY INFORMATION</h3>
                  <p><strong>Processing Time:</strong> {newGeneratedQuote.processing_time}</p>
                  <p><strong>Delivery Time:</strong> {newGeneratedQuote.delivery_time}</p>
                  <p><strong>Delivery Method:</strong> {newGeneratedQuote.delivery_method}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">PAYMENT TERMS</h3>
                  <p>{newGeneratedQuote.payment_terms}</p>
                  {companySettings.paypal_email && <p className="mt-2"><strong>PayPal:</strong> {companySettings.paypal_email}</p>}
                  {companySettings.bank_details && <p className="mt-2 text-sm whitespace-pre-wrap">{companySettings.bank_details}</p>}
                </div>
              </div>
              
              {newGeneratedQuote.notes && (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">NOTES</h3>
                  <p className="whitespace-pre-wrap">{newGeneratedQuote.notes}</p>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-4 mt-6 pt-4 border-t">
              <Button 
                onClick={downloadPDF} 
                disabled={generatingPDF}
                className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="download-pdf-btn"
              >
                {generatingPDF ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating PDF...</>
                ) : (
                  <><Download className="w-4 h-4" /> Download PDF</>
                )}
              </Button>
              <Button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white" data-testid="print-quote-btn">
                <Printer className="w-4 h-4" /> Print
              </Button>
              <Button onClick={() => setShowQuotePreview(false)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800" data-testid="close-preview-btn-bottom">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Company Settings Section */}
      <div 
        className="p-6 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Building className="w-6 h-6" style={{ color: isDark ? '#ff00ff' : '#ff1493' }} />
            <div>
              <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                Your Company Settings
              </h3>
              <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>
                These settings appear in quotes, footer, and contact sections
              </p>
            </div>
          </div>
          <Button
            onClick={saveCompanySettings}
            disabled={savingCompanySettings}
            size="sm"
            style={{ backgroundColor: isDark ? '#00ff88' : '#10b981', color: isDark ? '#000' : '#fff' }}
          >
            {savingCompanySettings ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Company Name</label>
            <input
              type="text"
              value={companySettings.company_name}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Company Name (Local)</label>
            <input
              type="text"
              value={companySettings.company_name_local}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name_local: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Contact Person</label>
            <input
              type="text"
              value={companySettings.contact_person}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, contact_person: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Address</label>
            <input
              type="text"
              value={companySettings.address}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, address: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Phone</label>
            <input
              type="text"
              value={companySettings.phone}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Email</label>
            <input
              type="email"
              value={companySettings.email}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, email: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>WhatsApp</label>
            <input
              type="text"
              value={companySettings.whatsapp}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, whatsapp: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>PayPal Email</label>
            <input
              type="email"
              value={companySettings.paypal_email}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, paypal_email: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Default Currency</label>
            <select
              value={companySettings.default_currency}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, default_currency: e.target.value }))}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            >
              <option value="USD">USD ($)</option>
              <option value="JPY">JPY (¥)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>Bank Details</label>
            <textarea
              value={companySettings.bank_details}
              onChange={(e) => setCompanySettings(prev => ({ ...prev, bank_details: e.target.value }))}
              rows={2}
              className="w-full p-2 rounded-lg text-sm"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              placeholder="Bank name, account number, etc."
            />
          </div>
        </div>
      </div>

      {/* Quote Form Section */}
      <div 
        className="p-6 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
            <div>
              <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                {editingGeneratedQuote ? `Edit Quote: ${editingGeneratedQuote.quote_number}` : 'Create New Quote'}
              </h3>
              {newGeneratedQuote.quote_request_id && (
                <p className="text-xs flex items-center gap-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                  Linked to Quote Request
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {/* Currency Selector - Always visible */}
            <div className="flex items-center gap-2 mr-4">
              <DollarSign className="w-4 h-4" style={{ color: isDark ? '#ffff00' : '#eab308' }} />
              <select 
                value={newGeneratedQuote.currency} 
                onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, currency: e.target.value }))} 
                className="px-3 py-1.5 rounded text-sm font-medium"
                style={{ 
                  backgroundColor: isDark ? '#1a1a1a' : '#fff', 
                  color: isDark ? '#ffff00' : '#eab308', 
                  border: `2px solid ${isDark ? '#ffff00' : '#eab308'}` 
                }}
                data-testid="currency-selector-top"
              >
                <option value="JPY">¥ JPY</option>
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
              </select>
            </div>
            <Button onClick={() => setShowQuotePreview(true)} size="sm" variant="outline" className="flex items-center gap-1">
              <Eye className="w-4 h-4" /> Preview
            </Button>
            <Button onClick={resetQuoteForm} size="sm" variant="outline">
              Clear Form
            </Button>
          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#ff00ff' : '#ff1493' }}>
            <User className="w-4 h-4" /> Customer Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <input type="text" placeholder="Customer Name *" value={newGeneratedQuote.customer_name} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_name: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
            <input type="text" placeholder="Company" value={newGeneratedQuote.customer_company} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_company: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
            <input type="text" placeholder="Contact Person" value={newGeneratedQuote.customer_contact} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_contact: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
            <input type="text" placeholder="Address" value={newGeneratedQuote.customer_address} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_address: e.target.value }))} className="p-2 rounded text-sm md:col-span-2" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
            <input type="text" placeholder="Phone" value={newGeneratedQuote.customer_phone} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_phone: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
            <input type="email" placeholder="Email" value={newGeneratedQuote.customer_email} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_email: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
            <input type="text" placeholder="WhatsApp" value={newGeneratedQuote.customer_whatsapp} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, customer_whatsapp: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
          </div>
        </div>

        {/* Quote Options */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
            <Check className="w-4 h-4" /> Quote Options
          </h4>
          <div 
            className="p-4 rounded-lg"
            style={{ 
              backgroundColor: isDark ? 'rgba(0, 255, 255, 0.05)' : 'rgba(14, 165, 233, 0.05)',
              border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` 
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Installation Location */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                  Installation Location
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewGeneratedQuote(prev => ({ ...prev, installation_location: 'inside' }))}
                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-all ${
                      newGeneratedQuote.installation_location === 'inside' ? 'scale-105' : ''
                    }`}
                    style={{
                      backgroundColor: newGeneratedQuote.installation_location === 'inside'
                        ? (isDark ? 'rgba(0, 255, 136, 0.2)' : 'rgba(16, 185, 129, 0.2)')
                        : (isDark ? '#1a1a1a' : '#f8fafc'),
                      border: `2px solid ${newGeneratedQuote.installation_location === 'inside'
                        ? (isDark ? '#00ff88' : '#10b981')
                        : (isDark ? '#333' : '#e2e8f0')}`,
                      color: newGeneratedQuote.installation_location === 'inside'
                        ? (isDark ? '#00ff88' : '#10b981')
                        : (isDark ? '#888' : '#64748b')
                    }}
                    data-testid="installation-inside-btn"
                  >
                    Inside
                  </button>
                  <button
                    onClick={() => setNewGeneratedQuote(prev => ({ ...prev, installation_location: 'outside' }))}
                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-all ${
                      newGeneratedQuote.installation_location === 'outside' ? 'scale-105' : ''
                    }`}
                    style={{
                      backgroundColor: newGeneratedQuote.installation_location === 'outside'
                        ? (isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(217, 70, 239, 0.2)')
                        : (isDark ? '#1a1a1a' : '#f8fafc'),
                      border: `2px solid ${newGeneratedQuote.installation_location === 'outside'
                        ? (isDark ? '#ff00ff' : '#d946ef')
                        : (isDark ? '#333' : '#e2e8f0')}`,
                      color: newGeneratedQuote.installation_location === 'outside'
                        ? (isDark ? '#ff00ff' : '#d946ef')
                        : (isDark ? '#888' : '#64748b')
                    }}
                    data-testid="installation-outside-btn"
                  >
                    Outside
                  </button>
                </div>
              </div>

              {/* Mounting Style */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                  Mounting Style
                </label>
                <input
                  type="text"
                  placeholder="e.g., Wall mount, Hanging, Stand"
                  value={newGeneratedQuote.mounting_style}
                  onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, mounting_style: e.target.value }))}
                  className="w-full p-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: isDark ? '#1a1a1a' : '#fff',
                    color: isDark ? '#fff' : '#000',
                    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                  }}
                  data-testid="mounting-style-input"
                />
              </div>

              {/* 1 Year Warranty Checkbox */}
              <div className="flex items-center">
                <label 
                  className="flex items-center gap-3 cursor-pointer p-2 rounded-lg w-full"
                  style={{ 
                    backgroundColor: newGeneratedQuote.include_warranty 
                      ? (isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)')
                      : 'transparent'
                  }}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={newGeneratedQuote.include_warranty}
                      onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, include_warranty: e.target.checked }))}
                      className="sr-only"
                      data-testid="warranty-checkbox"
                    />
                    <div
                      className="w-6 h-6 rounded border-2 flex items-center justify-center transition-all"
                      style={{
                        borderColor: newGeneratedQuote.include_warranty
                          ? (isDark ? '#00ff88' : '#10b981')
                          : (isDark ? '#444' : '#cbd5e1'),
                        backgroundColor: newGeneratedQuote.include_warranty
                          ? (isDark ? '#00ff88' : '#10b981')
                          : 'transparent'
                      }}
                    >
                      {newGeneratedQuote.include_warranty && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium" style={{ color: isDark ? '#ccc' : '#475569' }}>
                    1 Year Warranty
                  </span>
                </label>
              </div>

              {/* Include Shipping Toggle */}
              <div className="flex items-center">
                <label 
                  className="flex items-center gap-3 cursor-pointer p-2 rounded-lg w-full"
                  style={{ 
                    backgroundColor: newGeneratedQuote.include_shipping 
                      ? (isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(14, 165, 233, 0.1)')
                      : 'transparent'
                  }}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={newGeneratedQuote.include_shipping}
                      onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, include_shipping: e.target.checked }))}
                      className="sr-only"
                      data-testid="shipping-checkbox"
                    />
                    <div
                      className="w-6 h-6 rounded border-2 flex items-center justify-center transition-all"
                      style={{
                        borderColor: newGeneratedQuote.include_shipping
                          ? (isDark ? '#00ffff' : '#0ea5e9')
                          : (isDark ? '#444' : '#cbd5e1'),
                        backgroundColor: newGeneratedQuote.include_shipping
                          ? (isDark ? '#00ffff' : '#0ea5e9')
                          : 'transparent'
                      }}
                    >
                      {newGeneratedQuote.include_shipping && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium" style={{ color: isDark ? '#ccc' : '#475569' }}>
                    Include Shipping
                  </span>
                </label>
              </div>

              {/* Separate Items Toggle */}
              <div className="flex items-center">
                <label 
                  className="flex items-center gap-3 cursor-pointer p-2 rounded-lg w-full"
                  style={{ 
                    backgroundColor: newGeneratedQuote.separate_items 
                      ? (isDark ? 'rgba(255, 165, 0, 0.1)' : 'rgba(251, 146, 60, 0.1)')
                      : 'transparent'
                  }}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={newGeneratedQuote.separate_items || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewGeneratedQuote(prev => ({ ...prev, separate_items: checked }));
                        if (checked) setTimeout(ensureOptions, 0);
                      }}
                      className="sr-only"
                      data-testid="separate-items-checkbox"
                    />
                    <div
                      className="w-6 h-6 rounded border-2 flex items-center justify-center transition-all"
                      style={{
                        borderColor: newGeneratedQuote.separate_items
                          ? (isDark ? '#ffa500' : '#f59e0b')
                          : (isDark ? '#444' : '#cbd5e1'),
                        backgroundColor: newGeneratedQuote.separate_items
                          ? (isDark ? '#ffa500' : '#f59e0b')
                          : 'transparent'
                      }}
                    >
                      {newGeneratedQuote.separate_items && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium" style={{ color: isDark ? '#ccc' : '#475569' }}>
                    Separate Options
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items / Options */}
        <div className="mb-6">
          {!newGeneratedQuote.separate_items ? (
            /* Combined mode - single list of line items */
            <>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                  <Package className="w-4 h-4" /> Line Items
                </h4>
                <Button onClick={addLineItem} size="sm" variant="outline" className="flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {newGeneratedQuote.line_items.map((item) => (
                  <div key={item.id} className="p-3 rounded-lg" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <input type="text" placeholder="Type" value={item.item_type} onChange={(e) => updateLineItem(item.id, 'item_type', e.target.value)} className="col-span-2 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                      <input type="text" placeholder="Item Name *" value={item.item_name} onChange={(e) => updateLineItem(item.id, 'item_name', e.target.value)} className="col-span-3 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                      <input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)} className="col-span-1 p-2 rounded text-sm text-center" style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                      <input type="number" placeholder="Price" value={item.unit_price} onChange={(e) => updateLineItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)} className="col-span-2 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                      <div className="col-span-3 text-right font-semibold p-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                        {formatCurrency(item.quantity * item.unit_price, newGeneratedQuote.currency)}
                      </div>
                      <button onClick={() => removeLineItem(item.id)} className="col-span-1 p-2 text-red-500 hover:text-red-400" disabled={newGeneratedQuote.line_items.length <= 1}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <textarea placeholder="Description / Details" value={item.description} onChange={(e) => updateLineItem(item.id, 'description', e.target.value)} rows={2} className="w-full mt-2 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* Separate Options mode - tabbed interface */
            <>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: isDark ? '#ffa500' : '#ea580c' }}>
                  <Package className="w-4 h-4" /> Quote Options
                </h4>
              </div>
              {/* Option Tabs */}
              <div className="flex items-center gap-1 mb-4 flex-wrap" data-testid="option-tabs">
                {(newGeneratedQuote.options || []).map((opt, idx) => {
                  const t = calcOptionTotal(opt);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setActiveOptionIdx(idx)}
                      className="px-4 py-2 rounded-t-lg text-sm font-medium transition-all relative"
                      style={{
                        backgroundColor: activeOptionIdx === idx 
                          ? (isDark ? '#ffa500' : '#f59e0b')
                          : (isDark ? '#222' : '#f1f5f9'),
                        color: activeOptionIdx === idx
                          ? (isDark ? '#000' : '#fff')
                          : (isDark ? '#888' : '#64748b'),
                        border: `1px solid ${activeOptionIdx === idx ? (isDark ? '#ffa500' : '#f59e0b') : (isDark ? '#333' : '#e2e8f0')}`,
                        borderBottom: activeOptionIdx === idx ? 'none' : undefined,
                      }}
                      data-testid={`option-tab-${idx}`}
                    >
                      {opt.name || `Option ${idx + 1}`}
                      <span className="ml-2 text-xs opacity-75">({formatCurrency(t.total, newGeneratedQuote.currency)})</span>
                    </button>
                  );
                })}
                <button
                  onClick={addOption}
                  className="px-3 py-2 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#00ff88' : '#10b981', border: `1px dashed ${isDark ? '#00ff88' : '#10b981'}` }}
                  data-testid="add-option-btn"
                >
                  <Plus className="w-4 h-4 inline" /> Add Option
                </button>
              </div>

              {/* Active Option Content */}
              {(newGeneratedQuote.options || []).map((opt, optIdx) => {
                if (optIdx !== activeOptionIdx) return null;
                const t = calcOptionTotal(opt);
                return (
                  <div key={opt.id} className="p-4 rounded-lg" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `2px solid ${isDark ? '#ffa500' : '#f59e0b'}`, borderTopLeftRadius: 0 }}>
                    {/* Option header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt.name || ''}
                          onChange={(e) => updateOption(optIdx, 'name', e.target.value)}
                          className="px-2 py-1 rounded text-sm font-bold"
                          style={{ backgroundColor: 'transparent', color: isDark ? '#ffa500' : '#ea580c', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}`, width: '180px' }}
                          placeholder="Option name"
                          data-testid={`option-name-${optIdx}`}
                        />
                        <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: isDark ? 'rgba(0,255,136,0.1)' : 'rgba(16,185,129,0.1)', color: isDark ? '#00ff88' : '#10b981' }}>
                          Total: {formatCurrency(t.total, newGeneratedQuote.currency)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button onClick={() => addOptionLineItem(optIdx)} size="sm" variant="outline" className="flex items-center gap-1 text-xs">
                          <Plus className="w-3 h-3" /> Add Item
                        </Button>
                        {(newGeneratedQuote.options || []).length > 1 && (
                          <button onClick={() => removeOption(optIdx)} className="p-1 text-red-500 hover:text-red-400 text-xs" title="Remove Option">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Option line items */}
                    <div className="space-y-2 mb-3">
                      {(opt.line_items || []).map((item) => (
                        <div key={item.id} className="p-2 rounded" style={{ backgroundColor: isDark ? '#222' : '#f8fafc', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
                          <div className="grid grid-cols-12 gap-2 items-start">
                            <input type="text" placeholder="Type" value={item.item_type} onChange={(e) => updateOptionLineItem(optIdx, item.id, 'item_type', e.target.value)} className="col-span-2 p-1.5 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                            <input type="text" placeholder="Item Name *" value={item.item_name} onChange={(e) => updateOptionLineItem(optIdx, item.id, 'item_name', e.target.value)} className="col-span-3 p-1.5 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                            <input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateOptionLineItem(optIdx, item.id, 'quantity', parseInt(e.target.value) || 1)} className="col-span-1 p-1.5 rounded text-sm text-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                            <input type="number" placeholder="Price" value={item.unit_price} onChange={(e) => updateOptionLineItem(optIdx, item.id, 'unit_price', parseFloat(e.target.value) || 0)} className="col-span-2 p-1.5 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                            <div className="col-span-3 text-right font-semibold p-1.5 text-sm" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                              {formatCurrency((item.quantity || 0) * (item.unit_price || 0), newGeneratedQuote.currency)}
                            </div>
                            <button onClick={() => removeOptionLineItem(optIdx, item.id)} className="col-span-1 p-1.5 text-red-500 hover:text-red-400" disabled={opt.line_items.length <= 1}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          <textarea placeholder="Description / Details" value={item.description} onChange={(e) => updateOptionLineItem(optIdx, item.id, 'description', e.target.value)} rows={1} className="w-full mt-1 p-1.5 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                        </div>
                      ))}
                    </div>

                    {/* Option shipping & tax */}
                    <div className="grid grid-cols-3 gap-3 pt-3 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium" style={{ color: isDark ? '#888' : '#64748b' }}>Shipping:</label>
                        <input type="number" value={opt.shipping_cost || 0} onChange={(e) => updateOption(optIdx, 'shipping_cost', parseFloat(e.target.value) || 0)} className="flex-1 p-1.5 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium" style={{ color: isDark ? '#888' : '#64748b' }}>Tax %:</label>
                        <input type="number" value={opt.tax_rate || 0} onChange={(e) => updateOption(optIdx, 'tax_rate', parseFloat(e.target.value) || 0)} className="flex-1 p-1.5 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }} />
                      </div>
                      <div className="text-right">
                        <div className="text-xs" style={{ color: isDark ? '#888' : '#94a3b8' }}>Sub: {formatCurrency(t.subtotal, newGeneratedQuote.currency)}</div>
                        {t.shipping > 0 && <div className="text-xs" style={{ color: isDark ? '#888' : '#94a3b8' }}>+Ship: {formatCurrency(t.shipping, newGeneratedQuote.currency)}</div>}
                        {t.tax > 0 && <div className="text-xs" style={{ color: isDark ? '#888' : '#94a3b8' }}>+Tax: {formatCurrency(t.tax, newGeneratedQuote.currency)}</div>}
                        <div className="font-bold text-sm mt-1" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                          {formatCurrency(t.total, newGeneratedQuote.currency)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Image Upload Section */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
            <Image className="w-4 h-4" /> Product Images
          </h4>
          
          {/* Uploaded Images Preview */}
          {newGeneratedQuote.images && newGeneratedQuote.images.length > 0 && (
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3 mb-4">
              {newGeneratedQuote.images.map((img) => (
                <div 
                  key={img.id} 
                  className="relative group rounded-lg overflow-hidden"
                  style={{ border: `2px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                >
                  <img 
                    src={img.url?.startsWith('http') ? img.url : `${API_URL}${img.url}`} 
                    alt={img.name}
                    className="w-full h-20 object-cover"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23ddd" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">No Image</text></svg>'; }}
                  />
                  <button
                    onClick={() => removeQuoteImage(img.id)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <p className="text-xs p-1 truncate" style={{ color: isDark ? '#888' : '#64748b', backgroundColor: isDark ? '#1a1a1a' : '#f8fafc' }}>
                    {img.name}
                  </p>
                </div>
              ))}
            </div>
          )}
          
          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${isDraggingQuoteImage ? 'scale-102' : ''}`}
            style={{
              borderColor: isDraggingQuoteImage 
                ? (isDark ? '#ff00ff' : '#d946ef')
                : (isDark ? '#444' : '#cbd5e1'),
              backgroundColor: isDraggingQuoteImage 
                ? (isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(217, 70, 239, 0.1)')
                : 'transparent'
            }}
            onDrop={handleQuoteImageDrop}
            onDragOver={handleQuoteImageDragOver}
            onDragLeave={handleQuoteImageDragLeave}
            onClick={() => quoteImageInputRef.current?.click()}
          >
            {uploadingQuoteImage ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full mb-2" />
                <p style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto mb-2" style={{ color: isDark ? '#666' : '#94a3b8' }} />
                <p style={{ color: isDark ? '#888' : '#64748b' }}>
                  {isDraggingQuoteImage ? 'Drop images here!' : 'Drag & drop images or click to upload'}
                </p>
                <p className="text-xs mt-1" style={{ color: isDark ? '#555' : '#a1a1aa' }}>
                  PNG, JPG, GIF up to 10MB
                </p>
              </>
            )}
            <input
              ref={quoteImageInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={(e) => handleQuoteImageUpload(Array.from(e.target.files))}
            />
          </div>
        </div>

        {/* Shipping, Tax & Totals */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
              <DollarSign className="w-4 h-4" /> Shipping & Tax
            </h4>
            <div className="space-y-2">
              {newGeneratedQuote.include_shipping && (
                <div className="flex items-center gap-2">
                  <label className="w-24 text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Shipping:</label>
                  <input type="number" value={newGeneratedQuote.shipping_cost} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))} className="flex-1 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} data-testid="shipping-cost-input" />
                </div>
              )}
              {!newGeneratedQuote.include_shipping && (
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255, 165, 0, 0.1)' : 'rgba(251, 146, 60, 0.1)' }}>
                  <AlertCircle className="w-4 h-4" style={{ color: isDark ? '#ffa500' : '#fb923c' }} />
                  <span className="text-sm" style={{ color: isDark ? '#ffa500' : '#fb923c' }}>Shipping excluded from this quote</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="w-24 text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Tax Rate %:</label>
                <input type="number" value={newGeneratedQuote.tax_rate} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))} className="flex-1 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-24 text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Currency:</label>
                <select value={newGeneratedQuote.currency} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, currency: e.target.value }))} className="flex-1 p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
                  <option value="USD">USD ($)</option>
                  <option value="JPY">JPY (¥)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)', border: `1px solid ${isDark ? '#00ff88' : '#10b981'}` }}>
            <h4 className="text-sm font-semibold mb-3" style={{ color: isDark ? '#00ff88' : '#10b981' }}>Quote Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal:</span><span>{formatCurrency(calculateQuoteTotals().subtotal, newGeneratedQuote.currency)}</span></div>
              <div className="flex justify-between">
                <span>Shipping:</span>
                <span style={{ color: !newGeneratedQuote.include_shipping ? (isDark ? '#888' : '#9ca3af') : undefined, fontStyle: !newGeneratedQuote.include_shipping ? 'italic' : 'normal' }}>
                  {newGeneratedQuote.include_shipping ? formatCurrency(newGeneratedQuote.shipping_cost, newGeneratedQuote.currency) : 'Not Included'}
                </span>
              </div>
              {newGeneratedQuote.tax_rate > 0 && <div className="flex justify-between"><span>Tax ({newGeneratedQuote.tax_rate}%):</span><span>{formatCurrency(calculateQuoteTotals().taxAmount, newGeneratedQuote.currency)}</span></div>}
              <div className="flex justify-between text-lg font-bold pt-2 border-t" style={{ borderColor: isDark ? '#00ff88' : '#10b981', color: isDark ? '#00ff88' : '#10b981' }}>
                <span>TOTAL:</span><span>{formatCurrency(newGeneratedQuote.include_shipping ? calculateQuoteTotals().total : (calculateQuoteTotals().subtotal + calculateQuoteTotals().taxAmount), newGeneratedQuote.currency)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery & Terms */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <input type="text" placeholder="Processing Time" value={newGeneratedQuote.processing_time} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, processing_time: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
          <input type="text" placeholder="Delivery Time" value={newGeneratedQuote.delivery_time} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, delivery_time: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
          <input type="text" placeholder="Delivery Method" value={newGeneratedQuote.delivery_method} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, delivery_method: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
          <input type="text" placeholder="Payment Terms" value={newGeneratedQuote.payment_terms} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, payment_terms: e.target.value }))} className="p-2 rounded text-sm" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />
        </div>
        
        <textarea placeholder="Additional Notes" value={newGeneratedQuote.notes} onChange={(e) => setNewGeneratedQuote(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="w-full p-2 rounded text-sm mb-4" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }} />

        {/* Save Button */}
        <Button
          onClick={saveGeneratedQuote}
          disabled={savingGeneratedQuote || !newGeneratedQuote.customer_name}
          className="w-full flex items-center justify-center gap-2 py-3"
          style={{ background: isDark ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)' : 'linear-gradient(135deg, #ff1493 0%, #0ea5e9 100%)', color: '#fff' }}
        >
          <Save className="w-5 h-5" />
          {savingGeneratedQuote ? 'Saving...' : (editingGeneratedQuote ? 'Update Quote' : 'Save Quote')}
        </Button>

        {/* Quote Request Options */}
        <div 
          className="mt-4 p-4 rounded-lg"
          style={{ 
            backgroundColor: isDark ? 'rgba(0, 255, 255, 0.05)' : 'rgba(14, 165, 233, 0.05)',
            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
          }}
        >
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
            <Link className="w-4 h-4" /> Quote Request Options
          </h4>
          
          {/* Show linked status if linked */}
          {newGeneratedQuote.quote_request_id && (
            <div 
              className="flex items-center justify-between p-3 rounded-lg mb-3"
              style={{ 
                backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`
              }}
            >
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                <span style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                  Linked to quote request
                  {quotes.find(q => q.id === newGeneratedQuote.quote_request_id)?.name && (
                    <span className="font-semibold ml-1">
                      ({quotes.find(q => q.id === newGeneratedQuote.quote_request_id)?.name})
                    </span>
                  )}
                </span>
              </div>
              <button 
                onClick={() => setNewGeneratedQuote(prev => ({ ...prev, quote_request_id: null }))}
                className="p-1 rounded hover:opacity-70"
                style={{ color: isDark ? '#ff6666' : '#dc2626' }}
                title="Unlink"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button
              onClick={() => setShowLinkModal(true)}
              variant="outline"
              className="flex-1 flex items-center justify-center gap-2"
              style={{ 
                borderColor: isDark ? '#00ffff' : '#0ea5e9',
                color: isDark ? '#00ffff' : '#0ea5e9'
              }}
              data-testid="link-quote-request-btn"
            >
              <Link className="w-4 h-4" />
              {newGeneratedQuote.quote_request_id ? 'Change Link' : 'Link to Existing'}
            </Button>
            
            {!newGeneratedQuote.quote_request_id && (
              <Button
                onClick={saveAsQuoteRequest}
                variant="outline"
                className="flex-1 flex items-center justify-center gap-2"
                style={{ 
                  borderColor: isDark ? '#ff00ff' : '#d946ef',
                  color: isDark ? '#ff00ff' : '#d946ef'
                }}
                disabled={!newGeneratedQuote.customer_name}
                data-testid="save-as-request-btn"
              >
                <Plus className="w-4 h-4" />
                Save as New Request
              </Button>
            )}
          </div>
          
          <p className="text-xs mt-2" style={{ color: isDark ? '#666' : '#94a3b8' }}>
            {newGeneratedQuote.quote_request_id 
              ? 'This quote is linked to a customer request. Edit will load images from the request.'
              : 'Link to an existing customer request or create a new one from this quote.'}
          </p>
        </div>

        {/* Link to Quote Request Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
              onClick={() => setShowLinkModal(false)} 
            />
            <div 
              className="relative w-full max-w-2xl max-h-[80vh] rounded-xl p-6 overflow-hidden flex flex-col"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff' }}
            >
              <button 
                onClick={() => setShowLinkModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-200"
                style={{ backgroundColor: isDark ? '#333' : '#f1f5f9' }}
              >
                <X className="w-5 h-5" style={{ color: isDark ? '#fff' : '#000' }} />
              </button>
              
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                <Link className="w-5 h-5" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                Link to Quote Request
              </h3>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by name, email, or sign type..."
                  value={linkSearchQuery}
                  onChange={(e) => setLinkSearchQuery(e.target.value)}
                  className="w-full p-3 rounded-lg"
                  style={{
                    backgroundColor: isDark ? '#222' : '#f8fafc',
                    color: isDark ? '#fff' : '#000',
                    border: `1px solid ${isDark ? '#444' : '#e2e8f0'}`
                  }}
                  data-testid="link-search-input"
                />
              </div>
              
              {/* Quote Requests List */}
              <div className="flex-1 overflow-y-auto space-y-2">
                {getFilteredQuoteRequests().length === 0 ? (
                  <p className="text-center py-8" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                    No quote requests found
                  </p>
                ) : (
                  getFilteredQuoteRequests().map((request) => (
                    <div
                      key={request.id}
                      onClick={() => linkToQuoteRequest(request)}
                      className="p-4 rounded-lg cursor-pointer transition-all hover:scale-[1.01]"
                      style={{
                        backgroundColor: isDark ? '#222' : '#f8fafc',
                        border: `1px solid ${
                          newGeneratedQuote.quote_request_id === request.id 
                            ? (isDark ? '#00ff88' : '#10b981')
                            : (isDark ? '#333' : '#e2e8f0')
                        }`
                      }}
                      data-testid={`link-request-${request.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold flex items-center gap-2" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                            {request.name}
                            {newGeneratedQuote.quote_request_id === request.id && (
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ 
                                  backgroundColor: isDark ? '#00ff88' : '#10b981',
                                  color: isDark ? '#000' : '#fff'
                                }}
                              >
                                Currently Linked
                              </span>
                            )}
                          </p>
                          <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
                            {request.email} • {request.sign_type}
                          </p>
                          {request.message && (
                            <p className="text-xs mt-1 truncate max-w-md" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                              "{request.message.substring(0, 100)}..."
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                            {new Date(request.created_at).toLocaleDateString()}
                          </p>
                          {request.attachments?.length > 0 && (
                            <p className="text-xs mt-1" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                              {request.attachments.length} attachment(s)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                <Button
                  onClick={() => setShowLinkModal(false)}
                  variant="outline"
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Saved Quotes List */}
      <div 
        className="p-6 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
        }}
      >
        <h3 className="font-semibold text-lg mb-4" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
          Saved Quotes ({generatedQuotes.length})
        </h3>
        
        {generatedQuotes.length === 0 ? (
          <p style={{ color: isDark ? '#666' : '#94a3b8' }}>No quotes generated yet.</p>
        ) : (
          <div className="space-y-3">
            {generatedQuotes.map((quote) => (
              <div 
                key={quote.id}
                className="p-4 rounded-lg flex items-center justify-between"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
              >
                <div>
                  <p className="font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                    {quote.quote_number} - {quote.customer_name || quote.customer_company}
                  </p>
                  <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
                    Total: {formatCurrency(quote.total || 0, quote.currency || 'USD')} {quote.currency} • {new Date(quote.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { editGeneratedQuote(quote); setShowQuotePreview(true); }}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => editGeneratedQuote(quote)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={() => deleteGeneratedQuote(quote.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteGeneratorTab;
